import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * Invoice billing source — mirrors `CommissionSource` from `@bravemove/types/commission`
 * (widened across F23/F35/F38/F39/F41). Kept local — `@bravemove/types` is NOT a dep
 * of `@bravemove/ui-web`. Spec source:
 * [`docs/design/screens/revenue-lib.jsx`](../../../../../docs/design/screens/revenue-lib.jsx)
 * §3 `SourceTag` (line 531) + the six `invoiceSourceCfg` sources (line 182).
 */
export type InvoiceSource =
  | 'venue_subscription'
  | 'venue_hybrid'
  | 'venue_commission'
  | 'venue_placement'
  | 'tournament_listing'
  | 'tournament_ticketing_commission';
export type SourceTagSize = 'sm' | 'md';

export interface SourceTagProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly source: InvoiceSource;
  readonly size?: SourceTagSize;
  /** Override the default short label. */
  readonly label?: string;
}

/**
 * Source→short-label+tone map. Tailwind can't see dynamically-built class names
 * through its purge, so every tone class is a static literal here.
 */
const SOURCE_STYLE: Record<InvoiceSource, { box: string; defaultLabel: string }> = {
  venue_subscription: {
    box: 'bg-accent/[0.08] text-ink border border-accent/30',
    defaultLabel: 'SUBSCRIPTION',
  },
  venue_hybrid: {
    box: 'bg-tertiary/[0.08] text-ink border border-tertiary/30',
    defaultLabel: 'HYBRID',
  },
  venue_commission: {
    box: 'bg-info/[0.08] text-ink border border-info/30',
    defaultLabel: 'COMMISSION',
  },
  venue_placement: {
    box: 'bg-secondary/[0.08] text-ink border border-secondary/30',
    defaultLabel: 'PLACEMENT',
  },
  tournament_listing: {
    box: 'bg-warning/[0.08] text-ink border border-warning/30',
    defaultLabel: 'TOURNAMENT',
  },
  tournament_ticketing_commission: {
    box: 'bg-success/[0.08] text-ink border border-success/30',
    defaultLabel: 'TICKETING',
  },
};

const SIZE: Record<SourceTagSize, string> = {
  sm: 'px-[7px] py-[2px] text-[9px]',
  md: 'px-[8px] py-[3px] text-[9px]',
};

/**
 * F23 / F38 / F39 / F41 — `SourceTag` (shared primitive). The line-item billing
 * source tag on invoice rows in the admin statement queue + venue commission view.
 *
 * @example
 *   <SourceTag source="venue_subscription" />
 *   <SourceTag source="venue_placement" size="sm" />
 */
export function SourceTag({ source, size = 'md', label, className, ...rest }: SourceTagProps) {
  const style = SOURCE_STYLE[source];
  const text = label ?? style.defaultLabel;
  return (
    <span
      role="img"
      aria-label={`Billing source: ${text}`}
      data-testid={`source-tag-${source}`}
      className={cn(
        'inline-flex items-center rounded uppercase whitespace-nowrap align-middle font-mono font-bold tracking-[0.08em]',
        style.box,
        SIZE[size],
        className,
      )}
      {...rest}
    >
      <span>{text}</span>
    </span>
  );
}
